import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plane, PlusCircle, Send, Users, Building2, Mail, Phone, DollarSign,
  Calendar as CalIcon, Sparkles, ShieldCheck, Loader2, Radar, Check, X,
  Radio, ArrowRight, Edit3, Trash2, Copy, ExternalLink, Clock, TrendingUp, Zap,
} from "lucide-react";

/**
 * OperatorHub — the "one roof" operator command center rendered inside the
 * Cockpit page below the operator's Profile. Sub-sections:
 *   #ops-overview       stat row + PJP contact card
 *   #ops-fleet          add + manage aircraft
 *   #ops-empty-legs     post empty legs + reservation inbox
 *   #ops-empty-program  enroll / manage the empty-leg program
 *   #ops-charter        charter request inbox + my bids
 */
export default function OperatorHub({ user }) {
  const [dash, setDash] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [mySlots, setMySlots] = useState([]);
  const [myReservations, setMyReservations] = useState([]);
  const [openRequests, setOpenRequests] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showAircraftModal, setShowAircraftModal] = useState(false);
  const [showLegModal, setShowLegModal] = useState(false);
  const [bidForRequestId, setBidForRequestId] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const reload = async () => {
    try {
      const [d, f, sl, res, cr, mb] = await Promise.all([
        api.get("/operators/me/dashboard"),
        api.get("/operators/me/fleet"),
        api.get("/operators/me/empty-legs"),
        api.get("/operators/me/reservations"),
        api.get("/charter-requests?status=open&limit=25"),
        api.get("/operators/me/bids"),
      ]);
      setDash(d.data);
      setFleet(f.data.fleet || []);
      setMySlots(sl.data.slots || []);
      setMyReservations(res.data.reservations || []);
      setOpenRequests(cr.data.requests || []);
      setMyBids(mb.data.bids || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("This section is for charter operators.");
      }
    } finally { setBusy(false); }
  };

  useEffect(() => { reload(); }, []);

  const confirmReservation = async (slotId, reservationId) => {
    try {
      await api.post(`/operators/me/reservations/${slotId}/${reservationId}/confirm`);
      toast.success("Reservation confirmed. Rider notified.");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't confirm."); }
  };

  const enrollProgram = async () => {
    setEnrolling(true);
    try {
      await api.post("/empty-leg/operator/join", {
        typical_empty_leg_routes: [],
        aircraft_tiers_available: [],
        avg_seats_per_leg: 8,
        avg_empty_legs_per_month: 4,
        payout_email: user?.email,
      });
      toast.success("You're enrolled in the JumpSeat Program.");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enrollment failed."); }
    finally { setEnrolling(false); }
  };

  if (busy && !dash) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-cyan" /></div>;
  }

  return (
    <div className="cockpit-section pt-4" data-testid="operator-hub">

      {/* ═══════════ #ops-overview — stats + PJP contact card ═══════════ */}
      <div id="ops-overview" className="scroll-mt-24">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// OPERATIONS · COMMAND CENTER</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Your operator hub</h2>
        <p className="text-white/60 mt-1 text-sm">Everything a rider or PJP quote needs from you — all under one roof.</p>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          <Stat label="Fleet" value={dash?.fleet_count ?? 0} icon={Plane} testId="op-stat-fleet" />
          <Stat label="Active JumpSeats" value={dash?.active_empty_legs ?? 0} icon={Sparkles} testId="op-stat-legs" />
          <Stat label="Pending reservations" value={dash?.pending_reservations ?? 0} icon={Users} tone={dash?.pending_reservations ? "warn" : "cyan"} testId="op-stat-pending" />
          <Stat label="Open PJP RFQs" value={dash?.open_charter_requests ?? 0} icon={Radar} testId="op-stat-rfqs" />
          <Stat label="My active bids" value={dash?.my_active_bids ?? 0} icon={DollarSign} testId="op-stat-bids" />
        </div>

        <OperatorContactCard user={user} operator={dash?.operator} />
      </div>

      {/* ═══════════ #ops-fleet ═══════════ */}
      <div id="ops-fleet" className="scroll-mt-24 mt-14">
        <SectionHeader
          overline="// FLEET · YOUR AIRCRAFT"
          title={`${fleet.length} aircraft on file`}
          blurb="Every aircraft you can dispatch. Riders see these on the JumpSeat calendar and on charter bids."
          cta={<button type="button" onClick={() => setShowAircraftModal(true)} data-testid="op-add-aircraft" className="btn-hud btn-hud-solid"><PlusCircle className="h-3.5 w-3.5" /> ADD AIRCRAFT</button>}
        />
        {fleet.length === 0 && (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center">
            <Plane className="h-8 w-8 text-white/40 mx-auto mb-3" />
            <div className="text-white/60 text-sm">You haven't listed any aircraft yet. Add your first tail so PJP can start routing charter demand to you.</div>
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4" data-testid="op-fleet-grid">
          {fleet.map((a) => <AircraftCard key={a.id} a={a} onChange={reload} />)}
        </div>
      </div>

      {/* ═══════════ #ops-empty-legs — post + reservation inbox ═══════════ */}
      <div id="ops-empty-legs" className="scroll-mt-24 mt-14">
        <SectionHeader
          overline="// JUMPSEAT · POST + RESERVATIONS"
          title={`${mySlots.length} JumpSeats posted`}
          blurb="Post ferry legs before they fly empty. Riders on the network can request seats — you accept and PJP handles the paperwork."
          cta={
            <button type="button" onClick={() => setShowLegModal(true)} data-testid="op-add-leg" className="btn-hud btn-hud-solid" disabled={fleet.length === 0}>
              <PlusCircle className="h-3.5 w-3.5" /> POST JUMPSEAT
            </button>
          }
        />
        {fleet.length === 0 && (
          <div className="mono text-[11px] text-amber-300/90 mb-3">
            Add an aircraft first — you'll pick the type when you post a leg.
          </div>
        )}

        {/* Pending reservation inbox — always visible if any */}
        {myReservations.length > 0 && (
          <div className="mb-6 rounded-xl border p-4"
            style={{ borderColor: "rgba(46,213,115,0.35)", background: "rgba(46,213,115,0.06)" }}
            data-testid="op-reservation-inbox">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="mono text-[10px] tracking-widest uppercase" style={{ color: "#5FE895" }}>
                // RESERVATION INBOX · {myReservations.filter(r => r.reservation.status === "requested").length} pending
              </div>
            </div>
            <div className="space-y-2">
              {myReservations.slice(0, 8).map((r) => (
                <div key={r.reservation.id} className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 flex items-center gap-3 flex-wrap">
                  <div className="mono text-[11px] tracking-widest text-cyan">{r.departure_date}</div>
                  <div className="font-display font-semibold text-white">
                    {r.origin_icao} <ArrowRight className="inline h-3.5 w-3.5 opacity-60" /> {r.destination_icao}
                  </div>
                  <div className="text-[12px] text-white/60">{r.aircraft_type} · {r.reservation.first_name} {r.reservation.last_name} · {r.reservation.seats} seat{r.reservation.seats > 1 ? "s" : ""}</div>
                  <div className="ml-auto flex gap-2 items-center">
                    <a href={`mailto:${r.reservation.email}`} data-testid={`op-res-email-${r.reservation.id}`} title="Email rider" className="mono text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-white/15 text-white/70 hover:bg-white/5 inline-flex gap-1 items-center">
                      <Mail className="h-3 w-3" /> EMAIL
                    </a>
                    {r.reservation.status === "requested" ? (
                      <button
                        type="button"
                        onClick={() => confirmReservation(r.slot_id, r.reservation.id)}
                        data-testid={`op-res-confirm-${r.reservation.id}`}
                        className="btn-hud btn-hud-solid text-[10px]"
                      >
                        <Check className="h-3 w-3" /> CONFIRM
                      </button>
                    ) : (
                      <span className="mono text-[10px] tracking-widest uppercase text-emerald-300">
                        <Check className="h-3 w-3 inline" /> CONFIRMED
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My posted legs */}
        <div className="grid gap-2" data-testid="op-legs-list">
          {mySlots.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center">
              <Sparkles className="h-8 w-8 text-white/40 mx-auto mb-3" />
              <div className="text-white/60 text-sm">No JumpSeats posted yet. Every ferry you post gets first-look from 26,500+ verified members.</div>
            </div>
          ) : mySlots.map((s) => (
            <div key={s.id} className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 flex items-center gap-4 flex-wrap">
              <span className={`mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${s.is_active ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/15 text-white/50"}`}>
                {s.is_active ? "LIVE" : "PAUSED"}
              </span>
              <div className="mono text-[11px] tracking-widest text-cyan">
                {s.departure_date}{s.departure_time_local && ` · ${s.departure_time_local}`}
              </div>
              <div className="font-display font-semibold text-white">
                {s.origin_icao} <ArrowRight className="inline h-3.5 w-3.5 opacity-60" /> {s.destination_icao}
              </div>
              <div className="text-[12px] text-white/60">{s.aircraft_type}</div>
              <div className="mono text-[10.5px] text-white/70">
                <Users className="inline h-3 w-3 mr-0.5" />{s.seats_reserved}/{s.seats_available} reserved
              </div>
              <div className="font-display font-bold text-cyan ml-auto">${(s.seat_price_usd || 0).toLocaleString()}/seat</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════ #ops-empty-program ═══════════ */}
      <div id="ops-empty-program" className="scroll-mt-24 mt-14">
        <JumpseatPartnerCard
          enrolled={dash?.empty_leg_program_status === "active"}
          enrolling={enrolling}
          onEnroll={enrollProgram}
        />
      </div>

      {/* ═══════════ #ops-charter — RFQ inbox + my bids ═══════════ */}
      <div id="ops-charter" className="scroll-mt-24 mt-14">
        <SectionHeader
          overline="// CHARTER REQUESTS · PJP QUOTE ENGINE"
          title={`${openRequests.length} open RFQs waiting for bids`}
          blurb="Every Instant PJP Quote lands here. Bid directly, or email the client with a formal quote using their contact info."
          cta={<Link to="/quote" data-testid="op-view-all-rfqs" className="btn-hud"><ExternalLink className="h-3.5 w-3.5" /> QUOTE ENGINE</Link>}
        />

        <div className="grid gap-3" data-testid="op-rfq-inbox">
          {openRequests.length === 0 && (
            <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center text-white/60 text-sm">
              No open requests right now — check back or run{" "}
              <Link to="/aog" className="underline text-cyan">AOG Watch</Link> for urgent lifts.
            </div>
          )}
          {openRequests.slice(0, 12).map((r) => (
            <RfqRow key={r.id} rfq={r} onBid={() => setBidForRequestId(r.id)} myBids={myBids} />
          ))}
        </div>

        {/* My bids ledger */}
        {myBids.length > 0 && (
          <>
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 mt-8 mb-2">// MY BIDS</div>
            <div className="grid gap-2" data-testid="op-my-bids">
              {myBids.slice(0, 10).map((row) => (
                <div key={row.bid.id} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 flex items-center gap-3 flex-wrap">
                  <span className={`mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${
                    row.request.awarded_bid_id === row.bid.id ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" :
                    row.request.status === "closed" ? "border-white/15 text-white/50" :
                    "border-cyan/40 text-cyan bg-cyan/5"
                  }`}>
                    {row.request.awarded_bid_id === row.bid.id ? "WON" : row.request.status === "closed" ? "CLOSED" : "ACTIVE"}
                  </span>
                  <div className="font-display font-semibold text-white text-sm">
                    {(row.request.routing || []).map(l => `${l.origin_icao}→${l.destination_icao}`).join(" · ") || "trip"}
                  </div>
                  <div className="mono text-[11px] text-white/60">
                    {row.request.customer_first_name} {row.request.customer_last_name}
                  </div>
                  <div className="ml-auto font-display font-bold text-cyan">${(row.bid.price_usd || 0).toLocaleString()}</div>
                  <Link to={`/charter-requests/${row.bid.request_id}`} className="mono text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-white/15 text-white/70 hover:bg-white/5 inline-flex items-center gap-1">
                    OPEN <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ═══════════ modals ═══════════ */}
      {showAircraftModal && <AddAircraftModal onClose={() => setShowAircraftModal(false)} onSaved={() => { setShowAircraftModal(false); reload(); }} />}
      {showLegModal && <PostEmptyLegModal fleet={fleet} onClose={() => setShowLegModal(false)} onSaved={() => { setShowLegModal(false); reload(); }} />}
      {bidForRequestId && <BidModal requestId={bidForRequestId} fleet={fleet} onClose={() => setBidForRequestId(null)} onSaved={() => { setBidForRequestId(null); reload(); }} />}
    </div>
  );
}

// ── Contact card ─────────────────────────────────────────────────────────────
function OperatorContactCard({ user, operator }) {
  const rp = operator?.role_profile || {};
  const memberNum = String(operator?.member_number || user?.pjp_member_number || "").padStart(5, "0");
  const directEmail = `ops+${(user?.id || "").slice(0, 8)}@privatejetpilots.com`;

  const copy = (txt, label = "Copied") => {
    navigator.clipboard.writeText(txt).then(() => toast.success(`${label} to clipboard`));
  };

  return (
    <div className="mt-6 rounded-2xl border p-5 md:p-6 relative overflow-hidden"
      style={{ borderColor: "rgba(75,156,255,0.35)", background: "linear-gradient(140deg, rgba(75,156,255,0.10) 0%, rgba(5,8,16,0.9) 60%)" }}
      data-testid="op-contact-card"
    >
      <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan">// PJP · HOW CLIENTS REACH YOU</div>
      <div className="mt-2 flex flex-wrap items-baseline gap-3">
        <div className="font-display text-2xl font-bold text-white">{rp.company_name || `${user?.first_name} ${user?.last_name}`}</div>
        <span className="mono text-[10px] tracking-widest uppercase text-white/50">PJP-OP · {memberNum}</span>
      </div>
      {rp.dba && <div className="text-[12px] text-white/60 mt-0.5">dba {rp.dba}</div>}

      <div className="mt-5 grid md:grid-cols-3 gap-3">
        <ContactRow icon={Mail} label="Direct email" value={user?.email} onCopy={() => copy(user?.email, "Email")} testId="op-contact-email" />
        <ContactRow icon={Phone} label="Phone" value={user?.phone || "Not set"} onCopy={user?.phone ? (() => copy(user.phone, "Phone")) : null} testId="op-contact-phone" />
        <ContactRow icon={Radio} label="PJP quote inbox" value={directEmail} onCopy={() => copy(directEmail, "Quote inbox")} testId="op-contact-quote-inbox" />
        <ContactRow icon={ShieldCheck} label="Part 135 cert" value={rp.faa_certificate_number || "Not on file"} testId="op-contact-cert" />
        <ContactRow icon={Building2} label="DOT #" value={rp.dot_number || "Not on file"} testId="op-contact-dot" />
        <ContactRow icon={Sparkles} label="ARG/US · IS-BAO" value={[rp.argus_rating, rp.isbao_rating].filter(Boolean).join(" · ") || "Not on file"} testId="op-contact-safety" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <a href="#profile" className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-white/15 text-white/60 hover:bg-white/5 inline-flex items-center gap-1.5">
          <Edit3 className="h-3 w-3" /> EDIT MY PROFILE
        </a>
        <Link to={`/operators/${user?.id}`} data-testid="op-view-public" className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1.5">
          <ExternalLink className="h-3 w-3" /> VIEW PUBLIC LISTING
        </Link>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, onCopy, testId }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/40 p-3" data-testid={testId}>
      <div className="mono text-[9.5px] tracking-widest uppercase text-white/50 mb-1 flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="flex items-center gap-2">
        <div className="text-[13px] text-white/90 truncate">{value}</div>
        {onCopy && (
          <button type="button" onClick={onCopy} className="ml-auto text-white/50 hover:text-white" title="Copy">
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── stat pill ─────────────────────────────────────────────────────────────────
function Stat({ label, value, icon: Icon, tone = "cyan", testId }) {
  const color = tone === "warn" ? "#F4C443" : tone === "green" ? "#5FE895" : "#4B9CFF";
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-3" data-testid={testId}>
      <div className="mono text-[9px] tracking-widest uppercase text-white/50 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</div>
      <div className="font-display text-2xl font-bold mt-1 leading-none" style={{ color }}>{value}</div>
    </div>
  );
}

function SectionHeader({ overline, title, blurb, cta }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
      <div>
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">{overline}</div>
        <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">{title}</h3>
        {blurb && <div className="text-white/60 text-[13px] mt-1 max-w-2xl">{blurb}</div>}
      </div>
      {cta && <div className="flex-shrink-0">{cta}</div>}
    </div>
  );
}

// ── Aircraft ────────────────────────────────────────────────────────────────
function AircraftCard({ a, onChange }) {
  const remove = async () => {
    if (!confirm(`Remove ${a.tail_number || a.aircraft_type} from your fleet?`)) return;
    try {
      await api.delete(`/operators/aircraft/${a.id}`);
      toast.success("Aircraft removed");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove."); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`op-aircraft-${a.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display text-lg font-semibold text-white truncate">{a.aircraft_type}</div>
          <div className="mono text-[11px] tracking-widest text-cyan mt-0.5">{a.tail_number || "TAIL TBD"}</div>
        </div>
        <button type="button" onClick={remove} title="Remove" className="text-white/40 hover:text-red-400">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-white/60">
        <div><span className="text-white/40">Seats</span> · {a.max_pax || "—"}</div>
        <div><span className="text-white/40">Base</span> · {a.base_icao || "—"}</div>
        <div><span className="text-white/40">Range</span> · {a.range_nm || "—"} nm</div>
        <div><span className="text-white/40">Rate</span> · ${(a.hourly_rate_usd || 0).toLocaleString()}/hr</div>
      </div>
    </div>
  );
}

function AddAircraftModal({ onClose, onSaved }) {
  const [f, setF] = useState({ aircraft_type: "", tail_number: "", max_pax: 8, base_icao: "", range_nm: 3500, hourly_rate_usd: 12000 });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.aircraft_type) { toast.error("Aircraft type is required."); return; }
    setBusy(true);
    try {
      await api.post("/operators/aircraft", f);
      toast.success("Aircraft added to fleet.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Add aircraft" overline="// FLEET · NEW TAIL" testId="op-add-aircraft-modal">
      <Grid>
        <Field label="Aircraft type" value={f.aircraft_type} onChange={(v) => setF({ ...f, aircraft_type: v })} placeholder="G550, Citation X, Phenom 300…" tid="op-af-type" />
        <Field label="Tail number" value={f.tail_number} onChange={(v) => setF({ ...f, tail_number: v.toUpperCase() })} placeholder="N123AB" tid="op-af-tail" />
        <Field label="Max pax" type="number" value={f.max_pax} onChange={(v) => setF({ ...f, max_pax: parseInt(v || "0", 10) })} tid="op-af-pax" />
        <Field label="Home base (ICAO)" value={f.base_icao} onChange={(v) => setF({ ...f, base_icao: v.toUpperCase() })} placeholder="KTEB" tid="op-af-base" />
        <Field label="Range (nm)" type="number" value={f.range_nm} onChange={(v) => setF({ ...f, range_nm: parseInt(v || "0", 10) })} tid="op-af-range" />
        <Field label="Hourly rate (USD)" type="number" value={f.hourly_rate_usd} onChange={(v) => setF({ ...f, hourly_rate_usd: parseInt(v || "0", 10) })} tid="op-af-rate" />
      </Grid>
      <ModalActions onCancel={onClose} onSave={save} busy={busy} tid="op-af-save" />
    </Modal>
  );
}

// ── Empty-leg poster ────────────────────────────────────────────────────────
function PostEmptyLegModal({ fleet, onClose, onSaved }) {
  const iso = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({
    origin_icao: "", destination_icao: "",
    departure_date: iso, departure_time_local: "10:00",
    aircraft_type: fleet[0]?.aircraft_type || "",
    tail_number: fleet[0]?.tail_number || "",
    seats_available: fleet[0]?.max_pax || 6,
    seat_price_usd: 2500, empty_leg_notes: "",
    is_active: true,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.origin_icao || !f.destination_icao) { toast.error("Origin + destination required."); return; }
    setBusy(true);
    try {
      await api.post("/empty-leg/slots", f);
      toast.success("Empty leg posted — riders will see it now.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed."); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Post a JumpSeat" overline="// JUMPSEAT · NEW POST" testId="op-post-leg-modal">
      <Grid>
        <Field label="Origin ICAO" value={f.origin_icao} onChange={(v) => setF({ ...f, origin_icao: v.toUpperCase() })} placeholder="KTEB" tid="op-leg-orig" />
        <Field label="Destination ICAO" value={f.destination_icao} onChange={(v) => setF({ ...f, destination_icao: v.toUpperCase() })} placeholder="KMIA" tid="op-leg-dest" />
        <Field label="Date" type="date" value={f.departure_date} onChange={(v) => setF({ ...f, departure_date: v })} tid="op-leg-date" />
        <Field label="Local time" value={f.departure_time_local} onChange={(v) => setF({ ...f, departure_time_local: v })} placeholder="10:00" tid="op-leg-time" />
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Aircraft</div>
          <select value={f.aircraft_type} onChange={(e) => {
            const chosen = fleet.find(a => a.aircraft_type === e.target.value);
            setF({ ...f, aircraft_type: e.target.value, tail_number: chosen?.tail_number || "", seats_available: chosen?.max_pax || f.seats_available });
          }} className="input w-full" data-testid="op-leg-aircraft">
            {fleet.map(a => <option key={a.id} value={a.aircraft_type}>{a.aircraft_type} · {a.tail_number || "TBD"}</option>)}
          </select>
        </div>
        <Field label="Seats available" type="number" value={f.seats_available} onChange={(v) => setF({ ...f, seats_available: parseInt(v || "0", 10) })} tid="op-leg-seats" />
        <Field label="Seat price (USD)" type="number" value={f.seat_price_usd} onChange={(v) => setF({ ...f, seat_price_usd: parseInt(v || "0", 10) })} tid="op-leg-price" />
      </Grid>
      <div className="mt-3">
        <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Notes (optional)</div>
        <textarea rows={2} value={f.empty_leg_notes} onChange={(e) => setF({ ...f, empty_leg_notes: e.target.value })} className="input w-full" placeholder="Catering, pets, +1 policy…" data-testid="op-leg-notes" />
      </div>
      <ModalActions onCancel={onClose} onSave={save} busy={busy} tid="op-leg-save" />
    </Modal>
  );
}

// ── RFQ row + bid modal ──────────────────────────────────────────────────────
function RfqRow({ rfq, onBid, myBids }) {
  const routing = rfq.routing || [];
  const mine = myBids.find(b => b.bid.request_id === rfq.id);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] hover:border-cyan/30 transition-colors p-3" data-testid={`op-rfq-${rfq.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="mono text-[11px] tracking-widest text-cyan">
          {routing[0]?.departure_date || rfq.created_at?.slice(0, 10)}
        </div>
        <div className="font-display font-semibold text-white">
          {routing.map((l, i) => (
            <span key={i}>{i > 0 && <span className="text-white/40 mx-1">·</span>}{l.origin_icao} <ArrowRight className="inline h-3.5 w-3.5 opacity-60" /> {l.destination_icao}</span>
          ))}
        </div>
        <div className="text-[12px] text-white/60">
          <Users className="inline h-3 w-3 mr-0.5" />{rfq.pax_count} pax
        </div>
        <div className="text-[12px] text-white/60">
          {rfq.customer_first_name} {rfq.customer_last_name}
        </div>
        <div className="ml-auto flex gap-2 items-center">
          {rfq.customer_email && (
            <a href={`mailto:${rfq.customer_email}?subject=Charter%20quote%20from%20PJP%20operator`}
              data-testid={`op-rfq-email-${rfq.id}`}
              className="mono text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-white/15 text-white/70 hover:bg-white/5 inline-flex gap-1 items-center">
              <Mail className="h-3 w-3" /> EMAIL QUOTE
            </a>
          )}
          {mine ? (
            <span className="mono text-[10px] tracking-widest uppercase text-emerald-300 px-2">
              <Check className="h-3 w-3 inline" /> Your bid · ${(mine.bid.price_usd || 0).toLocaleString()}
            </span>
          ) : (
            <button type="button" onClick={onBid} data-testid={`op-rfq-bid-${rfq.id}`} className="btn-hud btn-hud-solid text-[10px]">
              <Send className="h-3 w-3" /> PLACE BID
            </button>
          )}
        </div>
      </div>
      {rfq.pjp_estimate_usd && (
        <div className="mt-2 mono text-[10.5px] text-white/50">
          PJP instant estimate · ${rfq.pjp_estimate_usd.toLocaleString()}
        </div>
      )}
    </div>
  );
}

function BidModal({ requestId, fleet, onClose, onSaved }) {
  const [f, setF] = useState({ aircraft_id: fleet[0]?.id || "", price_usd: 45000, includes: [], notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.aircraft_id) { toast.error("Pick an aircraft."); return; }
    setBusy(true);
    try {
      await api.post(`/charter-requests/${requestId}/bids`, { request_id: requestId, ...f });
      toast.success("Bid submitted.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bid failed."); }
    finally { setBusy(false); }
  };
  const toggleInclude = (v) => setF((p) => ({ ...p, includes: p.includes.includes(v) ? p.includes.filter(x => x !== v) : [...p.includes, v] }));
  const INCLUDES = ["Catering", "Ground transport", "De-icing", "Pets welcome", "Wi-Fi"];
  return (
    <Modal onClose={onClose} title="Place a bid" overline="// CHARTER · YOUR BID" testId="op-bid-modal">
      <Grid>
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Aircraft</div>
          <select value={f.aircraft_id} onChange={(e) => setF({ ...f, aircraft_id: e.target.value })} className="input w-full" data-testid="op-bid-aircraft">
            {fleet.map(a => <option key={a.id} value={a.id}>{a.aircraft_type} · {a.tail_number || "TBD"}</option>)}
          </select>
        </div>
        <Field label="All-in price (USD)" type="number" value={f.price_usd} onChange={(v) => setF({ ...f, price_usd: parseInt(v || "0", 10) })} tid="op-bid-price" />
      </Grid>
      <div className="mt-3">
        <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-2">Includes</div>
        <div className="flex flex-wrap gap-1.5">
          {INCLUDES.map((v) => (
            <button key={v} type="button" onClick={() => toggleInclude(v)} data-testid={`op-bid-inc-${v}`}
              className={`mono text-[10.5px] tracking-widest uppercase px-2.5 py-1 rounded-full border ${f.includes.includes(v) ? "border-cyan/70 bg-cyan/15 text-cyan" : "border-white/15 text-white/60"}`}
            >{v}</button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Notes to the client</div>
        <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="input w-full" placeholder="Trip specifics, safety ratings, exceptions…" data-testid="op-bid-notes" />
      </div>
      <ModalActions onCancel={onClose} onSave={save} busy={busy} tid="op-bid-save" />
    </Modal>
  );
}

// ── Reusable primitives ──────────────────────────────────────────────────────
function Modal({ onClose, title, overline, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-xl w-full max-h-[90vh] overflow-auto rounded-2xl border border-cyan/30 bg-obsidian p-6" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">{overline}</div>
        <div className="font-display text-2xl font-bold text-white mt-1 mb-4">{title}</div>
        {children}
      </div>
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-2 gap-3">{children}</div>; }
function Field({ label, value, onChange, type = "text", placeholder, tid }) {
  return (
    <div>
      <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">{label}</div>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="input w-full" data-testid={tid} />
    </div>
  );
}
function ModalActions({ onCancel, onSave, busy, tid }) {
  return (
    <div className="mt-6 flex gap-2 justify-end">
      <button type="button" onClick={onCancel} className="btn-hud">Cancel</button>
      <button type="button" onClick={onSave} disabled={busy} data-testid={tid} className="btn-hud btn-hud-solid">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5" /> SAVE</>}
      </button>
    </div>
  );
}

/**
 * JumpseatPartnerCard — glassmorphic pitch tile inside the Operator Hub.
 * Sells the four concrete perks of enrolling in the JumpSeat Program and
 * offers a one-tap ENROLL button. Flips to an "ENROLLED" state with the
 * live perks summary once the operator is in.
 */
function JumpseatPartnerCard({ enrolled, enrolling, onEnroll }) {
  const perks = [
    { icon: TrendingUp, label: "Boosted in Directory", detail: "Green ribbon + top of operator results." },
    { icon: DollarSign, label: "15% rev-share", detail: "Split the $10K/yr rider pool, paid quarterly." },
    { icon: Zap, label: "Fill empty legs fast", detail: "Auto-broadcast open legs to every paid rider." },
    { icon: ShieldCheck, label: "PJP-vetted riders only", detail: "Every rider is FAA/ID-verified. No no-shows." },
  ];

  return (
    <div className="relative isolate overflow-hidden" data-testid="op-jumpseat-partner-card">
      {/* Ambient green wash behind the glass */}
      <div
        aria-hidden="true"
        className="absolute -z-10 top-1/2 left-1/3 -translate-y-1/2 w-[520px] h-[520px] rounded-full opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(46,213,115,0.55) 0%, transparent 70%)", filter: "blur(90px)" }}
      />
      <div
        className={`relative rounded-2xl overflow-hidden border backdrop-blur-2xl backdrop-saturate-150 ${
          enrolled ? "border-avail/60" : "border-avail/35 hover:border-avail/60"
        } transition-colors`}
        style={{
          background: "linear-gradient(155deg, rgba(46,213,115,0.10) 0%, rgba(5,8,16,0.85) 60%)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 30px 60px -25px rgba(0,0,0,0.7), 0 0 90px -40px rgba(46,213,115,0.75)",
        }}
      >
        {/* Top light-catch */}
        <span
          aria-hidden="true"
          className="absolute top-0 left-6 right-6 h-px pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent, rgba(46,213,115,0.85), transparent)" }}
        />

        <div className="p-5 md:p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="mono text-[10px] tracking-[0.28em] uppercase text-avail flex items-center gap-2">
                // JUMPSEAT PROGRAM · REV-SHARE
                {enrolled && (
                  <span className="mono text-[9px] tracking-widest uppercase text-avail border border-avail/40 bg-avail/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Check className="h-2.5 w-2.5" /> ENROLLED
                  </span>
                )}
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight mt-1">
                {enrolled ? "You're in — welcome to the JumpSeat pool." : "Become a JumpSeat Partner · get boosted in Directory."}
              </h2>
              <p className="text-white/70 mt-2 max-w-2xl">
                {enrolled
                  ? "Your empty legs are broadcast to every paid Rider and your Directory card carries the green PARTNER ribbon."
                  : "Fill your empty legs with PJP-vetted, ID-verified Riders. Green ribbon on your Directory card, top placement in operator results, and a 15% cut of the annual rider pool."}
              </p>
            </div>
            <div className="flex-shrink-0">
              {enrolled ? (
                <Link
                  to="/rider/dashboard"
                  data-testid="op-open-jumpseat-manager"
                  className="mono text-[10px] tracking-widest uppercase px-4 py-2.5 rounded border border-avail/50 bg-avail/[0.10] text-avail hover:bg-avail/[0.18] flex items-center gap-1.5"
                >
                  Open JumpSeat Manager <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={onEnroll}
                  disabled={enrolling}
                  data-testid="op-enroll-program"
                  className="mono text-[10px] tracking-widest uppercase px-4 py-2.5 rounded font-bold text-obsidian bg-avail hover:brightness-110 disabled:opacity-60 flex items-center gap-1.5"
                  style={{ boxShadow: "0 0 24px rgba(46,213,115,0.55)" }}
                >
                  {enrolling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ShieldCheck className="h-3.5 w-3.5" /> Enroll now · one tap</>}
                </button>
              )}
            </div>
          </div>

          {/* Perks grid */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {perks.map(({ icon: Icon, label, detail }) => (
              <div
                key={label}
                className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-4"
              >
                <div className="flex items-center gap-2 text-avail">
                  <Icon className="h-4 w-4" />
                  <div className="mono text-[10px] tracking-widest uppercase">{label}</div>
                </div>
                <div className="text-[12.5px] text-white/70 leading-snug mt-1.5">{detail}</div>
              </div>
            ))}
          </div>

          {!enrolled && (
            <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center gap-2 text-[12px] text-white/55">
              <Sparkles className="h-3.5 w-3.5 text-avail" />
              <span>
                <span className="text-white/80">Zero setup, zero cost.</span> One tap and your empty legs start moving.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

